import { useAppSelector } from '@/store/hooks';
import {
  selectGreenroomMeeting,
  selectGreenroomMeetingLoading,
  selectWorkingProjectUuid,
} from '@/store/slices/project/selectors';
import { getGreenroomToken } from '@/store/slices/user/selectors';
import { ApiConfig, Showboard, type Environment, AppName } from '@brandlive/showboard';
import blAuth from '@/connection/bl-auth/bl-auth';
import Container from '@ui/container/container';
import ComingSoon from '@/micros/coming-soon/coming-soon';
import useSegmentsFeatureFlag from './use-segments-feature-flag';
import OptionalComponent from '@ui/optional-component/optional-component';

type TSegmentsProps = {
  appName?: AppName;
};

export default function Segments({ appName = 'platform' }: TSegmentsProps) {
  const greenroomMeeting = useAppSelector(selectGreenroomMeeting);
  const loadingGreenroomMeeting = useAppSelector(selectGreenroomMeetingLoading);
  const workingProjectUuid = useAppSelector(selectWorkingProjectUuid);
  const greenroomToken = useAppSelector(getGreenroomToken);
  const { segmentsEnabled, loading: loadingFlag } = useSegmentsFeatureFlag();
  const environment = import.meta.env.VITE_STAGE as Environment;

  if (!loadingFlag && !segmentsEnabled) return <ComingSoon label="Segments" />;

  const greenroomReady = !!greenroomMeeting && greenroomMeeting.event_uuid === workingProjectUuid;
  const isLoading = loadingGreenroomMeeting || loadingFlag || !greenroomReady;

  const showboardApiConfig: ApiConfig = {
    environment,
    roomId: greenroomMeeting?.meeting?.toString() ?? '',
    accessToken: blAuth.accessToken ?? '',
    idToken: blAuth.idToken ?? '',
    blToken: greenroomToken ?? '',
    autoLoad: true,
  };

  return (
    <Container
      data-component="Segments"
      skLoader={
        appName === 'platform-overview'
          ? {
              height: 112,
              width: 992,
              borderRadius: 8,
              zIndex: 2,
              loading: isLoading,
              design: 'GlobalCont1',
            }
          : undefined
      }
      opacity={appName === 'platform' && isLoading ? 0 : 1}
      style={{ transition: 'opacity 300ms ease-in-out' }}
    >
      <OptionalComponent display={greenroomReady}>
        <Showboard
          apiConfig={showboardApiConfig}
          appName={appName}
          shotThumbnailWidth={169}
          greenroomState={{ meeting: greenroomMeeting ?? undefined }}
        />
      </OptionalComponent>
    </Container>
  );
}
